import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Quote, Smartphone, Sparkles, Star, PenLine, ArrowUpRight, Building2, HeartHandshake, Compass } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { ShareButtons } from "@/components/ShareButtons";
import { FaLinkedin } from "react-icons/fa";
import { BookFlipbook, BookCoverFace, BookPageFace } from "@/components/BookFlipbook";
import { LivePreviewCard } from "@/components/LivePreviewCard";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PILLARS, QUOTES, MARQUEE_WORDS, TESTIMONIALS, FAQS, MEDIA } from "@/lib/content";
import { submitContact, fetchReviews, createReview, fetchFeed, submitInquiry, fetchSettings } from "@/lib/api";

const IMG = {
  leaf: "https://images.unsplash.com/photo-1588345349197-75132bb08664?q=80&w=1600&auto=format&fit=crop",
  author: "/brand/official.png",
  book: "https://customer-assets-jai6qajn.emergentagent.net/job_love-genome/artifacts/zabfwreo_The%20Love%20Genome%20Front%20Page%20-%20Flagship%20Book.png",
};

/* ---------------- HERO ---------------- */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen bg-emerald overflow-hidden flex items-center" data-testid="hero">
      {/* leaf texture layer */}
      <motion.div
        aria-hidden
        style={{ y: yImg }}
        className="absolute inset-0 opacity-20 mix-blend-luminosity"
      >
        <img src={IMG.leaf} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-emerald-deep/40 via-transparent to-emerald" />

      {/* floating motif */}
      <motion.div
        aria-hidden
        className="absolute right-[6%] top-1/2 -translate-y-1/2 hidden lg:block"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ y: yImg }}
      >
        <motion.div animate={{ y: [0, -18, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}>
          <Logo className="w-[26rem] h-[26rem] text-gold/90" stroke="#D4AF37" />
        </motion.div>
      </motion.div>

      <motion.div style={{ y: yText, opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-24">
        <motion.p
          className="eyebrow text-gold mb-8 flex items-center gap-3"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
        >
          <span className="w-10 h-px bg-gold inline-block" /> A Universal Framework by Felicia T. Johnson
        </motion.p>

        <h1 className="font-display text-cream leading-[0.92] tracking-tight text-[clamp(3rem,9vw,7.5rem)] max-w-5xl">
          <MaskedLines
            lines={["The Love", <span key="g" className="italic text-gold">Genome™</span>]}
            delay={0.35}
            stagger={0.14}
          />
        </h1>

        <motion.p
          className="font-editorial italic text-cream/85 text-xl md:text-3xl max-w-2xl mt-8 leading-snug"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 1 }}
        >
          How love becomes virtue, character, action, relationship — and human flourishing.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center gap-4 mt-12"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }}
        >
          <Link to="/store" className="btn-gold" data-testid="hero-shop-btn">
            Get the Book <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/read" className="btn-outline border-cream/30 text-cream hover:bg-cream hover:text-emerald hover:border-cream" data-testid="hero-read-btn">
            Read Chapters 1–2 Free
          </Link>
          <a href="#framework" className="btn-outline border-cream/30 text-cream hover:bg-cream hover:text-emerald hover:border-cream" data-testid="hero-framework-btn">
            Explore the Framework
          </a>
        </motion.div>

        <motion.div
          className="mt-10"
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.35, duration: 0.7 }}
        >
          <LivePreviewCard />
        </motion.div>
      </motion.div>

      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-cream/50 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}
      >
        <span className="eyebrow text-[0.6rem]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity }}>
          <ArrowDown className="w-4 h-4" />
        </motion.div>
      </motion.div>
    </section>
  );
};

/* ---------------- MARQUEE ---------------- */
const Marquee = () => (
  <section className="bg-cream py-10 border-y border-line overflow-hidden" aria-hidden data-testid="marquee">
    <div className="marquee-track">
      {[0, 1].map((rep) => (
        <div key={rep} className="flex items-center">
          {MARQUEE_WORDS.map((w, i) => (
            <span key={`${rep}-${i}`} className="font-display text-6xl md:text-8xl text-ink/90 px-8 flex items-center gap-8">
              {w}
              <span className="text-gold text-4xl">✦</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </section>
);

/* ---------------- BOOK OVERVIEW ---------------- */
const BookOverview = () => (
  <section id="book" className="bg-cream py-28 md:py-40" data-testid="book-overview">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-6 relative">
        <Reveal>
          <div className="relative bg-surface border border-line p-6 md:p-10">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t border-l border-gold" />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-gold" />
            <div className="relative w-full">
              <BookFlipbook
                testId="book-image"
                ariaLabel="The Love Genome — Volume I"
                pages={[
                  <BookCoverFace
                    key="cover"
                    src="https://customer-assets-wrfwihn1.emergentagent.net/job_36b1c8e6-f427-4ce9-850e-09559237efee/artifacts/evs1y7r8_ChatGPT%20Image%20Aug%2010%2C%202026%2C%2012_24_00%20PM.png"
                    alt="The Love Genome — Volume I: The Foundational Framework by Felicia T. Johnson"
                  />,
                  <BookPageFace
                    key="p1"
                    eyebrow="Preface · Page 1"
                    title="Every generation inherits the language of love."
                    pageNumber="i"
                    paragraphs={[
                      "We use it in homes, places of worship, schools, workplaces, hospitals, wedding ceremonies, funeral services, political speeches, songs, stories, and private conversations.",
                      "We say we love our children, our parents, our spouses, our friends, our communities, our countries, our work, and our beliefs.",
                      "Yet the same word can describe experiences with radically different consequences.",
                    ]}
                  />,
                  <BookPageFace
                    key="p2"
                    eyebrow="Preface · Page 2"
                    title="One word. Many meanings."
                    pageNumber="ii"
                    paragraphs={[
                      "One person uses love to mean affection. Another means sacrifice. Another means loyalty, provision, protection, attraction, duty, forgiveness, or belonging.",
                      "Some understand love as a feeling that arrives without invitation. Others experience it as a decision renewed through years of ordinary practice.",
                      "The word is familiar, but familiarity has not guaranteed clarity — and that is where The Love Genome™ begins.",
                    ]}
                  />,
                ]}
              />
            </div>
            <p className="text-center text-[11px] font-body text-ink/45 mt-4 tracking-widest uppercase">
              Turn the pages · Preface preview
            </p>
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-6">
        <Reveal>
          <p className="eyebrow text-emerald mb-6">The Book</p>
          <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight mb-8">
            A new way to understand love.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-body text-ink/70 text-lg leading-relaxed mb-6">
            The Love Genome™ moves beyond love as a mere emotion to present it as a comprehensive, organizing framework for human development and flourishing. Love is not just a feeling — it is a dynamic, living system.
          </p>
          <p className="font-body text-ink/70 text-lg leading-relaxed mb-10">
            Through personal narrative and insightful analysis, Felicia T. Johnson invites you to understand love as an interconnected tapestry — where virtues become the genes of love, shaping character, building relationships, and ultimately shaping communities that flourish.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="flex flex-wrap gap-x-10 gap-y-4 border-t border-line pt-8">
            {["500+ pages", "10 Parts · 26 Chapters", "33 Reflection Prompts"].map((f) => (
              <span key={f} className="font-body text-sm text-ink/60 flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-gold rounded-full" /> {f}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="eyebrow text-ink/40">Share</span>
            <ShareButtons title="The Love Genome™ by Felicia T. Johnson — a framework for human flourishing" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- FRAMEWORK ---------------- */
const Framework = () => (
  <section id="framework" className="bg-emerald text-cream py-28 md:py-40" data-testid="framework">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="grid lg:grid-cols-12 gap-10 mb-20 items-end">
        <div className="lg:col-span-8">
          <Reveal>
            <p className="eyebrow text-gold mb-6">The Framework · Five Pillars</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
              The architecture of<br /><span className="italic text-gold">The Love Genome™</span>
            </h2>
          </Reveal>
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.1}>
            <p className="font-editorial italic text-2xl text-cream/80 leading-snug">
              Five interconnected pillars — read them as chapters of a single, living idea.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="border-t border-cream/15">
        {PILLARS.map((p, i) => (
          <Reveal key={p.num} delay={i * 0.05}>
            <div className="group grid md:grid-cols-12 gap-6 md:gap-10 py-10 md:py-14 border-b border-cream/15 items-baseline transition-colors duration-500 hover:bg-emerald-deep/40 px-2 md:px-6 -mx-2 md:-mx-6">
              <div className="md:col-span-1 font-display text-3xl md:text-4xl text-gold">{p.num}</div>
              <div className="md:col-span-4">
                <h3 className="font-display text-3xl md:text-5xl leading-none transition-transform duration-500 group-hover:translate-x-2">
                  {p.name}
                </h3>
                <p className="eyebrow text-gold/80 mt-3">{p.role}</p>
              </div>
              <div className="md:col-span-7">
                <p className="font-body text-cream/70 text-lg leading-relaxed max-w-2xl">{p.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- QUOTES ---------------- */
const FeaturedQuotes = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], [80, -80]);
  return (
    <section ref={ref} className="bg-cream py-28 md:py-40 overflow-hidden" data-testid="quotes">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="eyebrow text-emerald mb-16 text-center">In Her Words</p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-20">
          {QUOTES.map((q, i) => (
            <Reveal key={i} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-24" : ""}>
              <div className="relative">
                <Quote className="w-10 h-10 text-gold mb-6" strokeWidth={1} />
                <p className="font-editorial italic text-3xl md:text-4xl text-ink leading-[1.15]">{q}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- AUTHOR ---------------- */
const Author = () => (
  <section id="author" className="bg-surface py-28 md:py-40" data-testid="author">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16 items-center">
      <div className="lg:col-span-5 order-2 lg:order-1">
        <Reveal>
          <p className="font-body text-xs md:text-sm font-bold uppercase tracking-[0.35em] text-emerald mb-4 inline-flex items-center gap-3" data-testid="author-eyebrow">
            <span className="w-8 h-px bg-gold" /> The Author
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-ink font-bold leading-[1.02] tracking-tight mb-3" data-testid="author-name">
            Felicia “Tosin” Johnson
          </h2>
          <span className="block w-20 h-1 bg-gold mb-8" aria-hidden />
          <p className="font-editorial italic text-2xl text-emerald mb-8 leading-snug">
            Founder & Chief Architect of The Love Genome™ — a framework for how love becomes human flourishing.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-body text-ink/70 text-lg leading-relaxed mb-6">
            Felicia’s journey spans continents, industries, and leadership roles — from beginning her career in Nigeria, to rebuilding her life in the United States, to leading people and organizational transformation within global corporations. Those experiences revealed a truth: while organizations invest in process and technology, lasting transformation begins with the human condition.
          </p>
          <p className="font-body text-ink/70 text-lg leading-relaxed mb-6">
            That realization became the catalyst for The Love Genome™ — a universal framework helping individuals, families, leaders, and communities understand how flourishing is cultivated from the inside out. As CEO of PathLights Global LLC and Founder of Boat of Life Inc. and The Workforce Lens™, she applies one unified vision across the workplace, community, and thought leadership.
          </p>
          <p className="font-body text-ink/70 text-lg leading-relaxed mb-10">
            A 2026 Influential Women Honoree, featured in Influential Women Magazine, and a proud ForbesBLK member, Felicia is advancing a new generation of leadership grounded in empathy, evidence, and enduring impact.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-3 gap-6 border-t border-line pt-8">
            {[["PathLights Global", "CEO"], ["Boat of Life Inc.", "Founder · 501(c)(3)"], ["The Workforce Lens™", "Founder"]].map(([n, l]) => (
              <div key={n}>
                <p className="font-display text-xl md:text-2xl text-emerald leading-tight">{n}</p>
                <p className="font-body text-xs text-ink/50 mt-1 uppercase tracking-wider">{l}</p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Profile links */}
        <Reveal delay={0.18}>
          <div className="mt-10 border-t border-line pt-8" data-testid="author-links">
            <p className="font-body text-[11px] font-bold uppercase tracking-[0.32em] text-emerald mb-5">
              Follow & connect
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="https://www.linkedin.com/in/ftosinjohnson"
                target="_blank" rel="noreferrer"
                data-testid="author-linkedin-personal"
                className="group inline-flex items-center gap-3 px-5 py-3 bg-emerald text-cream hover:bg-emerald-deep transition-colors"
              >
                <FaLinkedin className="w-5 h-5 text-gold" />
                <span className="text-left">
                  <span className="block font-body text-[10px] uppercase tracking-widest text-gold">LinkedIn</span>
                  <span className="block font-display text-sm">Felicia T. Johnson</span>
                </span>
              </a>
              <a
                href="https://www.linkedin.com/company/the-love-genome%E2%84%A2/"
                target="_blank" rel="noreferrer"
                data-testid="author-linkedin-company"
                className="group inline-flex items-center gap-3 px-5 py-3 bg-emerald text-cream hover:bg-emerald-deep transition-colors"
              >
                <FaLinkedin className="w-5 h-5 text-gold" />
                <span className="text-left">
                  <span className="block font-body text-[10px] uppercase tracking-widest text-gold">Company</span>
                  <span className="block font-display text-sm">The Love Genome™</span>
                </span>
              </a>
              <a
                href="https://influentialwomen.com/connect/felicia-johnson"
                target="_blank" rel="noreferrer"
                data-testid="author-influential-women"
                className="group inline-flex items-center gap-3 px-5 py-3 border-2 border-gold text-emerald hover:bg-gold hover:text-ink transition-colors"
              >
                <span className="w-6 h-6 rounded-full bg-gold flex items-center justify-center shrink-0">
                  <span className="font-display text-emerald text-sm font-bold">IW</span>
                </span>
                <span className="text-left">
                  <span className="block font-body text-[10px] uppercase tracking-widest">2026 Honoree</span>
                  <span className="block font-display text-sm font-bold">Influential Women</span>
                </span>
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.22}>
          <blockquote className="mt-10 border-l-2 border-gold pl-6">
            <p className="font-editorial italic text-2xl text-ink leading-snug">
              “Empathy is not softness; it is strategy. Reinvention is not a setback; it is a strategy. Human flourishing is not an accident; it is something we can intentionally cultivate.”
            </p>
            <cite className="font-body text-xs text-ink/50 uppercase tracking-wider mt-3 block not-italic">— Felicia Tosin Johnson</cite>
          </blockquote>
        </Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7 order-1 lg:order-2">
        <Reveal>
          <div className="relative">
            <img src={IMG.author} alt="Felicia T. Johnson" className="w-full aspect-[4/5] object-cover" data-testid="author-image" />
            <div className="absolute -bottom-5 -left-5 bg-gold p-5">
              <Logo className="w-10 h-10 text-emerald" stroke="#0F3821" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------------- TESTIMONIALS + REVIEWS ---------------- */
const Stars = ({ n }) => (
  <span className="flex gap-0.5" aria-label={`${n} out of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={`w-4 h-4 ${i < n ? "text-gold fill-gold" : "text-line"}`} strokeWidth={1.5} />
    ))}
  </span>
);

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", rating: 5, quote: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReviews().then(setReviews).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.quote.trim().length < 10) {
      toast.error("Please share at least a sentence.");
      return;
    }
    setLoading(true);
    try {
      const { review } = await createReview(form);
      setReviews((r) => [review, ...r]);
      toast.success("Thank you — your reflection is now shared.");
      setForm({ name: "", role: "", rating: 5, quote: "" });
      setOpen(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    ...reviews.map((r) => ({ quote: r.quote, name: r.name, role: r.role, rating: r.rating })),
    ...TESTIMONIALS,
  ];

  return (
    <section className="bg-cream py-28 md:py-40" data-testid="testimonials">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="eyebrow text-emerald mb-6">Praise & Reflections</p>
              <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight max-w-3xl">
                Words from those who lived it.
              </h2>
            </div>
            <button className="btn-emerald" onClick={() => setOpen((v) => !v)} data-testid="write-review-btn">
              {open ? "Close" : "Share your reflection"}
            </button>
          </div>
        </Reveal>

        {open && (
          <motion.form
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            onSubmit={submit} className="border border-line bg-surface p-8 md:p-12 mb-16"
            data-testid="review-form"
          >
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Your name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="review-name"
                  className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink transition-colors" />
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Role (optional)</label>
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="Reader, Educator, Leader…" data-testid="review-role"
                  className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink placeholder:text-ink/30 transition-colors" />
              </div>
            </div>
            <div className="mb-8">
              <label className="eyebrow text-ink/50 block mb-3">Rating</label>
              <div className="flex gap-2" data-testid="review-rating">
                {Array.from({ length: 5 }).map((_, i) => (
                  <button type="button" key={i} onClick={() => setForm({ ...form, rating: i + 1 })} aria-label={`${i + 1} stars`}>
                    <Star className={`w-7 h-7 transition-colors ${i < form.rating ? "text-gold fill-gold" : "text-line"}`} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-8">
              <label className="eyebrow text-ink/50 block mb-3">Your reflection</label>
              <textarea required rows={3} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })}
                data-testid="review-quote"
                className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink resize-none transition-colors" />
            </div>
            <button type="submit" disabled={loading} className="btn-emerald disabled:opacity-60" data-testid="review-submit">
              {loading ? "Sharing…" : "Publish reflection"}
            </button>
          </motion.form>
        )}

        <div className="grid md:grid-cols-2 gap-px bg-line border border-line" data-testid="reviews-grid">
          {cards.map((t, i) => (
            <Reveal key={i} delay={(i % 2) * 0.08}>
              <div className="bg-cream p-10 md:p-14 h-full flex flex-col justify-between min-h-[320px]">
                <div>
                  {t.rating ? <div className="mb-6"><Stars n={t.rating} /></div> : null}
                  <p className="font-editorial italic text-2xl md:text-3xl text-ink leading-snug">“{t.quote}”</p>
                </div>
                <div className="mt-10 flex items-center gap-4">
                  <span className="w-10 h-px bg-gold" />
                  <div>
                    <p className="font-display text-lg text-emerald">{t.name}</p>
                    <p className="font-body text-xs text-ink/50 uppercase tracking-wider mt-0.5">{t.role}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};


/* ---------------- MEDIA ---------------- */
const Media = () => (
  <section id="media" className="bg-emerald-deep text-cream py-28 md:py-36" data-testid="media">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <div>
            <p className="eyebrow text-gold mb-6">Media & Press</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
              The conversation is<br />just beginning.
            </h2>
          </div>
          <p className="font-body text-cream/60 max-w-sm">
            For interviews, speaking engagements, and press inquiries, reach out through the contact form below.
          </p>
        </div>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-px bg-cream/10 border border-cream/10">
        {MEDIA.map((m, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <div className="bg-emerald-deep p-10 h-full group hover:bg-emerald transition-colors duration-500">
              <p className="eyebrow text-gold mb-8">{m.label}</p>
              <h3 className="font-display text-2xl leading-snug mb-6 min-h-[5rem]">{m.title}</h3>
              <p className="font-body text-xs text-cream/50 uppercase tracking-wider">{m.meta}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- APP TEASER ---------------- */
const AppTeaser = () => (
  <section className="bg-cream py-28 md:py-40" data-testid="app-teaser">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="bg-surface border border-line p-10 md:p-20 grid lg:grid-cols-12 gap-12 items-center relative overflow-hidden">
          <div className="absolute -right-16 -top-16 opacity-5">
            <Logo className="w-72 h-72 text-emerald" stroke="#0F3821" />
          </div>
          <div className="lg:col-span-7 relative z-10">
            <p className="eyebrow text-emerald mb-6 flex items-center gap-3">
              <Smartphone className="w-4 h-4" /> Companion App · Coming Soon
            </p>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight mb-6">
              Practice the framework, every day.
            </h2>
            <p className="font-body text-ink/70 text-lg leading-relaxed max-w-xl mb-8">
              The Love Genome™ companion app brings daily virtue practices, honest self-examination prompts, and guided reflections to your pocket — turning insight into a way of living.
            </p>
            <a href="#contact" className="btn-emerald" data-testid="app-notify-btn">
              <Sparkles className="w-4 h-4" /> Get notified at launch
            </a>
          </div>
          <div className="lg:col-span-5 relative z-10 flex justify-center">
            <div className="w-56 h-[26rem] bg-emerald rounded-[2.5rem] border-4 border-emerald-deep p-4 shadow-2xl">
              <div className="w-full h-full bg-emerald-deep rounded-[1.8rem] flex flex-col items-center justify-center gap-6 p-6 text-center">
                <Logo className="w-16 h-16 text-gold" stroke="#D4AF37" />
                <p className="font-display text-cream text-xl">Today’s Virtue</p>
                <p className="font-editorial italic text-gold text-2xl">Compassion</p>
                <p className="font-body text-cream/50 text-xs">Choice by choice, day by day.</p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- FAQ ---------------- */
const FAQ = () => (
  <section id="faq" className="bg-surface py-28 md:py-40" data-testid="faq">
    <div className="max-w-[1000px] mx-auto px-6 md:px-10">
      <Reveal>
        <p className="eyebrow text-emerald mb-6">Questions</p>
        <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight mb-16">
          Frequently asked.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-t border-line last:border-b">
              <AccordionTrigger className="font-display text-xl md:text-2xl text-ink hover:text-emerald hover:no-underline py-7 text-left" data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="font-body text-ink/70 text-lg leading-relaxed pb-8 max-w-3xl">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);

/* ---------------- CONTACT ---------------- */
const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you — we'll be in touch soon.");
      setForm({ name: "", email: "", subject: "General Inquiry", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <section id="contact" className="bg-cream py-28 md:py-40" data-testid="contact">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow text-emerald mb-6">Contact</p>
            <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight mb-8">
              Let’s start a conversation.
            </h2>
            <p className="font-body text-ink/70 text-lg leading-relaxed mb-10 max-w-md">
              For speaking, media, bulk orders, or partnership inquiries — or simply to share how the framework has touched your life.
            </p>
            <div className="border-t border-line pt-8 space-y-4">
              <p className="font-editorial italic text-2xl text-emerald leading-snug">
                “To every person who has ever asked, what does it truly mean to love?”
              </p>
              <p className="font-body text-sm text-ink/50">— From the dedication</p>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <label className="eyebrow text-ink/50 block mb-3">Name</label>
                  <input required value={form.name} onChange={set("name")} data-testid="contact-name"
                    className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink transition-colors" />
                </div>
                <div>
                  <label className="eyebrow text-ink/50 block mb-3">Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} data-testid="contact-email"
                    className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink transition-colors" />
                </div>
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Subject</label>
                <input value={form.subject} onChange={set("subject")} data-testid="contact-subject"
                  className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink transition-colors" />
              </div>
              <div>
                <label className="eyebrow text-ink/50 block mb-3">Message</label>
                <textarea required rows={4} value={form.message} onChange={set("message")} data-testid="contact-message"
                  className="w-full bg-transparent border-b border-line focus:border-emerald outline-none py-3 font-body text-ink resize-none transition-colors" />
              </div>
              <button type="submit" disabled={loading} className="btn-emerald disabled:opacity-60" data-testid="contact-submit">
                {loading ? "Sending…" : <>Send Message <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ---------------- FEATURED CIRCLE ---------------- */
const FeaturedCircle = () => {
  const [circle, setCircle] = useState(null);
  useEffect(() => {
    fetchSettings().then((s) => setCircle(s.featured_circle)).catch(() => {});
  }, []);
  if (!circle) return null;
  return (
    <section className="bg-emerald-deep text-cream py-20 md:py-28" data-testid="featured-circle">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="border border-gold/30 p-10 md:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <p className="eyebrow text-gold mb-5">Featured Reader Circle</p>
              <h2 className="font-display text-3xl md:text-5xl leading-tight mb-3">{circle.name}</h2>
              <p className="font-editorial italic text-2xl text-cream/80">
                {circle.city}, {circle.country} · {circle.kind}{circle.schedule ? ` · ${circle.schedule}` : ""}
              </p>
              <p className="font-body text-cream/60 mt-2">Facilitated by {circle.facilitator}</p>
            </div>
            <Link to="/circles" className="btn-gold shrink-0" data-testid="featured-circle-cta">
              Find a circle near you <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- LATEST WRITING (RSS) ---------------- */
const LatestWriting = () => {
  const [state, setState] = useState({ loading: true, configured: false, items: [] });

  useEffect(() => {
    fetchFeed()
      .then((d) => setState({ loading: false, configured: !!d.configured, items: d.items || [] }))
      .catch(() => setState({ loading: false, configured: false, items: [] }));
  }, []);

  const hasItems = state.items.length > 0;

  return (
    <section id="writing" className="bg-cream py-28 md:py-40" data-testid="latest-writing">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
            <div>
              <p className="eyebrow text-emerald mb-6 flex items-center gap-3">
                <PenLine className="w-4 h-4" /> Latest From Felicia
              </p>
              <h2 className="font-display text-4xl md:text-6xl text-ink leading-[1.02] tracking-tight max-w-2xl">
                New writing & reflections.
              </h2>
            </div>
          </div>
        </Reveal>

        {state.loading ? (
          <div className="grid md:grid-cols-3 gap-px bg-line border border-line">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-cream h-56 animate-pulse" />
            ))}
          </div>
        ) : hasItems ? (
          <div className="grid md:grid-cols-3 gap-px bg-line border border-line" data-testid="writing-grid">
            {state.items.slice(0, 3).map((it, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <a href={it.link} target="_blank" rel="noopener noreferrer"
                  className="group bg-cream p-8 md:p-10 h-full flex flex-col hover:bg-surface transition-colors duration-500"
                  data-testid={`writing-item-${i}`}>
                  {it.published && <p className="eyebrow text-ink/40 mb-5">{new Date(it.published).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>}
                  <h3 className="font-display text-2xl text-ink leading-tight mb-4 group-hover:text-emerald transition-colors">{it.title}</h3>
                  <p className="font-body text-ink/60 text-sm leading-relaxed flex-1">{it.summary}</p>
                  <span className="mt-6 inline-flex items-center gap-2 font-body text-sm text-emerald">
                    Read more <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal>
            <div className="border border-line bg-surface p-12 md:p-20 text-center" data-testid="writing-empty">
              <PenLine className="w-10 h-10 text-emerald mx-auto mb-6" strokeWidth={1.2} />
              <p className="font-editorial italic text-3xl text-emerald mb-4">Fresh essays are on the way.</p>
              <p className="font-body text-ink/60 max-w-xl mx-auto mb-8">
                Subscribe to be the first to read new writing from Felicia on love, leadership, and human flourishing — delivered straight to your inbox.
              </p>
              <a href="#contact" className="btn-emerald">
                Join the newsletter <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};


/* ---------------- WORK WITH FELICIA ---------------- */
const VENTURES = [
  { Icon: Building2, name: "PathLights Global LLC", role: "Leadership & Organizational Transformation",
    text: "Executive consulting and workforce transformation that strengthen leadership, transform culture, and build resilient, high-performing teams." },
  { Icon: HeartHandshake, name: "Boat of Life Inc.", role: "Community · 501(c)(3) Nonprofit",
    text: "Restoring dignity and expanding opportunity for individuals and families navigating life's most significant transitions." },
  { Icon: Compass, name: "The Workforce Lens™", role: "Research & Thought Leadership",
    text: "Examining leadership psychology, organizational behavior, ethics, and the future of work through the lens of human flourishing." },
];

const SERVICES = [
  "PathLights Global — Leadership & Culture",
  "Boat of Life Inc. — Community Partnership",
  "The Workforce Lens™ — Research & Speaking",
  "Speaking / Keynote",
  "General Inquiry",
];

const WorkWithFelicia = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", service: SERVICES[0], message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitInquiry(form);
      toast.success("Thank you — Felicia's team will be in touch soon.");
      setForm({ name: "", email: "", organization: "", service: SERVICES[0], message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="work" className="bg-emerald text-cream py-28 md:py-40" data-testid="work-with-felicia">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow text-gold mb-6">Work With Felicia</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-tight">
                One framework,<br /><span className="italic text-gold">three ways to partner.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="font-editorial italic text-2xl text-cream/80 leading-snug">
                Bring The Love Genome™ to your organization, community, or stage.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-px bg-cream/10 border border-cream/10 mb-20">
          {VENTURES.map(({ Icon, name, role, text }, i) => (
            <Reveal key={name} delay={i * 0.08}>
              <div className="bg-emerald-deep p-10 h-full group hover:bg-emerald transition-colors duration-500" data-testid={`venture-${i}`}>
                <span className="w-12 h-12 border border-gold/40 flex items-center justify-center mb-8 text-gold group-hover:bg-gold group-hover:text-ink transition-colors duration-500">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl leading-tight mb-2">{name}</h3>
                <p className="eyebrow text-gold/80 mb-5">{role}</p>
                <p className="font-body text-cream/70 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="grid lg:grid-cols-12 gap-12 border-t border-cream/15 pt-16">
            <div className="lg:col-span-4">
              <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4">Start a conversation.</h3>
              <p className="font-body text-cream/70 leading-relaxed">
                Tell us a little about your goals and Felicia's team will respond personally.
              </p>
            </div>
            <div className="lg:col-span-8">
              <form onSubmit={submit} className="space-y-8" data-testid="inquiry-form">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow text-cream/50 block mb-3">Name</label>
                    <input required value={form.name} onChange={set("name")} data-testid="inquiry-name"
                      className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 font-body text-cream transition-colors" />
                  </div>
                  <div>
                    <label className="eyebrow text-cream/50 block mb-3">Email</label>
                    <input required type="email" value={form.email} onChange={set("email")} data-testid="inquiry-email"
                      className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 font-body text-cream transition-colors" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <label className="eyebrow text-cream/50 block mb-3">Organization (optional)</label>
                    <input value={form.organization} onChange={set("organization")} data-testid="inquiry-org"
                      className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 font-body text-cream transition-colors" />
                  </div>
                  <div>
                    <label className="eyebrow text-cream/50 block mb-3">How can we help?</label>
                    <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="inquiry-service" className="w-full bg-transparent border-0 border-b border-cream/25 rounded-none px-0 py-3 font-body text-cream focus:ring-0 focus:border-gold h-auto">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-cream border-line">
                        {SERVICES.map((s) => (
                          <SelectItem key={s} value={s} className="font-body text-ink">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="eyebrow text-cream/50 block mb-3">Message</label>
                  <textarea required rows={3} value={form.message} onChange={set("message")} data-testid="inquiry-message"
                    className="w-full bg-transparent border-b border-cream/25 focus:border-gold outline-none py-3 font-body text-cream resize-none transition-colors" />
                </div>
                <button type="submit" disabled={loading} className="btn-gold disabled:opacity-60" data-testid="inquiry-submit">
                  {loading ? "Sending…" : <>Send Inquiry <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};


export default function Home() {
  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <BookOverview />
      <Framework />
      <FeaturedQuotes />
      <Author />
      <Testimonials />
      <FeaturedCircle />
      <LatestWriting />
      <WorkWithFelicia />
      <Media />
      <AppTeaser />
      <FAQ />
      <Contact />
    </main>
  );
}
